import { List, Record } from "immutable";
import { AnyAction } from "redux";

import { PaymentAlias } from "@typings/payments";
import {
  Referral,
  AccountBalance,
  BasicInfo,
  CompanyInfo,
  ReviewInfo,
} from "@typings/profile";
import { LOADING_STATE } from "@typings/reduxThunkActions";
import { Subscription } from "@typings/subscription";

import {
  ACTION_FAIL,
  ACTION_NO_DATA,
  ACTION_PENDING,
  ACTION_SUCCESS,
} from "@services/AsyncActionCreatorFactory";

import {
  DELETE_PAYMENT_ALIAS,
  FETCH_ACCOUNT_BALANCE,
  FETCH_BASIC_INFO,
  FETCH_CANCELLATION_REASONS,
  FETCH_COMPANY_INFO,
  FETCH_CONTRACTS,
  FETCH_PAYMENT_ALIASES,
  FETCH_REFERRAL,
  FETCH_SUBSCRIPTIONS,
  SET_BASIC_INFO,
  SET_COMPANY_INFO,
  SET_DEFAULT_PAYMENT_ALIAS,
  FETCH_REVIEW_INFO,
} from "./profile.actions";
import {
  accountBalanceFactory,
  accountBalanceInitialState,
  basicInfoFactory,
  basicInfoInitialState,
  clientContractsFactory,
  clientSubscriptionsFactory,
  companyInfoFactory,
  companyInfoInitialState,
  contractCancellationReasonsFactory,
  defaultPaymentAliasFactory,
  deletePaymentAliasFactory,
  paymentAliasesFactory,
  referralFactory,
  referralInitialState,
  reviewInfoInitialState,
} from "./profile.factories";
import { ProfileReducer } from "./profile.typings";

const initialState = Record<ProfileReducer>({
  referral: Record<Referral>(referralInitialState)(),
  accountBalance: Record<AccountBalance>(accountBalanceInitialState)(),
  accountBalanceLoadingState: LOADING_STATE.IDLE,
  basicInfo: Record<BasicInfo>(basicInfoInitialState)(),
  paymentAliases: List<Record<PaymentAlias>>(),
  paymentAliasesLoadingState: LOADING_STATE.IDLE,
  subscriptions: List<Record<Subscription>>(),
  companyInfo: Record<CompanyInfo>(companyInfoInitialState)(),
  contracts: List(),
  contractsLoadingState: LOADING_STATE.IDLE,
  contractCancellationReasons: {},
  reviewInfo: Record<ReviewInfo>(reviewInfoInitialState)(),
  reviewInfoLoadingState: LOADING_STATE.IDLE,
});

const profileReducer = (
  store: Record<ProfileReducer> = initialState(),
  action: AnyAction,
): Record<ProfileReducer> => {
  const { type, payload, params } = action;

  switch (type) {
    case `${FETCH_ACCOUNT_BALANCE}${ACTION_PENDING}`: {
      return store.set("accountBalanceLoadingState", LOADING_STATE.LOADING);
    }
    case `${FETCH_ACCOUNT_BALANCE}${ACTION_FAIL}`: {
      return store.set("accountBalanceLoadingState", LOADING_STATE.FAILED);
    }
    case `${FETCH_ACCOUNT_BALANCE}${ACTION_SUCCESS}`: {
      return store
        .set("accountBalance", accountBalanceFactory(payload.data))
        .set("accountBalanceLoadingState", LOADING_STATE.SUCCEEDED);
    }
    case `${FETCH_REFERRAL}${ACTION_SUCCESS}`: {
      return store.set("referral", referralFactory(payload.data));
    }
    case `${FETCH_BASIC_INFO}${ACTION_SUCCESS}`: {
      return store.set("basicInfo", basicInfoFactory(payload.data));
    }
    case `${SET_BASIC_INFO}${ACTION_SUCCESS}`: {
      return store.set("basicInfo", basicInfoFactory(params));
    }
    case `${SET_COMPANY_INFO}${ACTION_SUCCESS}`: {
      return store.set("companyInfo", companyInfoFactory(params));
    }
    case `${FETCH_PAYMENT_ALIASES}${ACTION_PENDING}`: {
      return store.set("paymentAliasesLoadingState", LOADING_STATE.LOADING);
    }
    case `${FETCH_PAYMENT_ALIASES}${ACTION_FAIL}`: {
      return store.set("paymentAliasesLoadingState", LOADING_STATE.FAILED);
    }
    case `${FETCH_PAYMENT_ALIASES}${ACTION_SUCCESS}`: {
      return store
        .set("paymentAliases", paymentAliasesFactory(payload.data))
        .set("paymentAliasesLoadingState", LOADING_STATE.SUCCEEDED);
    }
    case `${DELETE_PAYMENT_ALIAS}${ACTION_NO_DATA}`: {
      return store.set(
        "paymentAliases",
        deletePaymentAliasFactory(
          store.get("paymentAliases"),
          params.paymentAliasUuid,
        ),
      );
    }
    case `${SET_DEFAULT_PAYMENT_ALIAS}${ACTION_SUCCESS}`: {
      return store.set(
        "paymentAliases",
        defaultPaymentAliasFactory(
          store.get("paymentAliases"),
          params.paymentAliasUuid,
        ),
      );
    }
    case `${FETCH_SUBSCRIPTIONS}${ACTION_SUCCESS}`: {
      return store.set(
        "subscriptions",
        clientSubscriptionsFactory(payload.data),
      );
    }
    case FETCH_CONTRACTS: {
      return store
        .set("contracts", clientContractsFactory([]))
        .set("contractsLoadingState", LOADING_STATE.LOADING);
    }
    case `${FETCH_CONTRACTS}${ACTION_SUCCESS}`: {
      return store
        .set("contracts", clientContractsFactory(payload.data))
        .set("contractsLoadingState", LOADING_STATE.SUCCEEDED);
    }
    case `${FETCH_CONTRACTS}${ACTION_FAIL}`: {
      return store
        .set("contracts", clientContractsFactory([]))
        .set("contractsLoadingState", LOADING_STATE.FAILED);
    }

    case `${FETCH_COMPANY_INFO}${ACTION_SUCCESS}`: {
      return store.set("companyInfo", companyInfoFactory(payload.data));
    }

    case `${FETCH_CANCELLATION_REASONS}${ACTION_SUCCESS}`: {
      return store.set("contractCancellationReasons", {
        ...store.get("contractCancellationReasons"),
        [action.params.contractUuid]: contractCancellationReasonsFactory(
          payload.data,
        ),
      });
    }

    case `${FETCH_REVIEW_INFO}${ACTION_PENDING}`: {
      return store.set("reviewInfoLoadingState", LOADING_STATE.LOADING);
    }
    case `${FETCH_REVIEW_INFO}${ACTION_FAIL}`: {
      return store.set("reviewInfoLoadingState", LOADING_STATE.FAILED);
    }
    case `${FETCH_REVIEW_INFO}${ACTION_SUCCESS}`: {
      return store
        .set("reviewInfo", Record<ReviewInfo>(payload.data)())
        .set("reviewInfoLoadingState", LOADING_STATE.SUCCEEDED);
    }

    default: {
      return store;
    }
  }
};

export { profileReducer };
